import { Button, HStack, Icon, IconButton, Menu, Portal, Spacer, Square, VStack } from '@chakra-ui/react'
import { AddIcon, CancelIcon, ChevronDownIcon, DeleteIcon } from '@editor/assets/icons'
import { BlueprintEditorTools } from '@editor/pages/projects/blueprint-editor/definitions'
import { RootState, useAppDispatch } from '@editor/store/app'
import { Tooltip } from '@infraloop/common'
import { useSelector } from 'react-redux'

import { setSelectedBlueprint } from '../../../../store/blueprintEditor'
import useCreateBlueprint from '../../hooks/useCreateBlueprint'

const ViewSwitcher = () => {
  // Contexts / Hooks
  const { handleStartCreatingBlueprint, handleBlueprintDelete, handleCancelCreatingBlueprint } = useCreateBlueprint()

  // Store
  const dispatch = useAppDispatch()

  const isAllowedModify = useSelector((state: RootState) => state.blueprint.editor.isAllowedToModify)
  const initCompleted = useSelector((state: RootState) => state.blueprint.editor.initCompleted)
  const blueprints = useSelector((state: RootState) => state.blueprint.editor.blueprints)
  const selectedBlueprint = useSelector((state: RootState) => state.blueprint.editor.selectedBlueprint)
  const isCreatingBlueprint = useSelector((state: RootState) => state.blueprint.editor.isCreatingBlueprint)
  const deletingBlueprintIds = useSelector((state: RootState) => state.blueprint.editor.deletingBlueprintIds)
  const selectedTool = useSelector((state: RootState) => state.blueprint.editor.selectedTool)

  const isModifyingMaskRegion = [BlueprintEditorTools.MaskPCD, BlueprintEditorTools.Focus].includes(selectedTool) // Focus is only available during PCD masking

  return (
    <VStack
      id="blueprint-view-switcher-container"
      position="absolute"
      top={2}
      left="50%"
      transform="translateX(-50%)"
      zIndex={20}
      w={{
        base: '385px',
        xl: '335px',
      }}
      gap={1}
    >
      <HStack position="relative" gap={1} h={{ base: 12, xl: 10 }} w="100%" id="blueprint-view-switcher">
        <Menu.Root variant="editorPanel" positioning={{ placement: 'bottom-start', sameWidth: true }}>
          <Menu.Trigger asChild>
            <Button
              backgroundColor="gray2.800"
              fontWeight="normal"
              color="white"
              fontSize="md"
              size="lg"
              _hover={{ bg: 'gray2.600' }}
              _expanded={{ bg: 'gray2.600' }}
              _focus={{ bg: 'gray2.600', boxShadow: 'outline' }}
              _active={{ bg: 'gray2.600' }}
              h="100%"
              disabled={!initCompleted || isCreatingBlueprint || blueprints.length === 0 || isModifyingMaskRegion}
              flex={1}
              px={4}
              justifyContent="space-between"
            >
              {selectedBlueprint?.name}
              <ChevronDownIcon />
            </Button>
          </Menu.Trigger>
          {blueprints.length > 0 && (
            <Portal>
              <Menu.Positioner>
                <Menu.Content minW={0}>
                  {blueprints.map((blueprint) => (
                    <Menu.Item
                      disabled={
                        !initCompleted || isCreatingBlueprint || deletingBlueprintIds.includes(blueprint.blueprint_id)
                      }
                      key={blueprint.blueprint_id}
                      value={blueprint.blueprint_id}
                      backgroundColor={
                        blueprint.blueprint_id === selectedBlueprint?.blueprint_id ? 'whiteAlpha.100' : 'gray2.700'
                      }
                      onClick={() => {
                        dispatch(setSelectedBlueprint(blueprint))
                      }}
                    >
                      {blueprint.name}
                      {isAllowedModify && (
                        <>
                          <Spacer />
                          <IconButton
                            aria-label="delete blueprint"
                            position="relative"
                            color="white"
                            fontSize={{ base: '2xl', xl: 'lg' }}
                            ml={4}
                            backgroundColor="whiteAlpha.100"
                            _hover={{ bg: 'whiteAlpha.400' }}
                            p={{ base: 2, xl: 1 }}
                            borderRadius="md"
                            overflow="hidden"
                            cursor="pointer"
                            loading={deletingBlueprintIds.includes(blueprint.blueprint_id)}
                            onClick={(e) => {
                              if (initCompleted) {
                                e.stopPropagation()
                                void handleBlueprintDelete(blueprint.blueprint_id)
                              }
                            }}
                          >
                            <Icon as={DeleteIcon} />
                          </IconButton>
                        </>
                      )}
                    </Menu.Item>
                  ))}
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          )}
        </Menu.Root>
        {!isCreatingBlueprint && isAllowedModify ? (
          <Tooltip
            showArrow
            positioning={{ placement: 'bottom' }}
            label="新しい設計図面を作成"
            // p={2}
            // fontSize="xs"
            // fontWeight="normal"
          >
            <IconButton
              aria-label="create blueprint"
              position="relative"
              color="white"
              backgroundColor="gray2.800"
              _hover={{ bg: 'gray2.600' }}
              borderRadius="md"
              overflow="hidden"
              cursor="pointer"
              w={{ base: 12, xl: 10 }}
              h={{ base: 12, xl: 10 }}
              disabled={!initCompleted || isCreatingBlueprint || isModifyingMaskRegion}
              onClick={handleStartCreatingBlueprint}
              loading={!initCompleted}
            >
              <Square fontSize={{ base: '2xl', xl: 'lg' }}>
                <AddIcon />
              </Square>
            </IconButton>
          </Tooltip>
        ) : null}
        {isCreatingBlueprint && (
          <Tooltip
            showArrow
            positioning={{ placement: 'bottom' }}
            label="キャンセル"
            // p={2}
            // fontSize="xs"
            // fontWeight="normal"
          >
            <IconButton
              aria-label="キャンセル"
              position="relative"
              color="white"
              backgroundColor="gray2.800"
              _hover={{ bg: 'gray2.600' }}
              borderRadius="md"
              overflow="hidden"
              cursor="pointer"
              w={{ base: 12, xl: 10 }}
              h={{ base: 12, xl: 10 }}
              onClick={handleCancelCreatingBlueprint}
              loading={!initCompleted}
            >
              <Square fontSize={{ base: '2xl', xl: 'lg' }}>
                <CancelIcon color="red" fontSize="1.2em" />
              </Square>
            </IconButton>
          </Tooltip>
        )}
      </HStack>
    </VStack>
  )
}

export default ViewSwitcher
